import {Injectable} from '@angular/core';
import {CytoscapeService} from '../cy-services/cytoscape.service';
import {NodeService} from '../../api/services/node.service';
import {MapService} from '../../api/services/map.service';
import {MapStateService} from '../../api/services/map-state.service';
import {CommentService} from '../../api/services/comment.service';
import {CommentsStateManager} from '../../shared/components/comments/comments.state-manager';
import {
  ClipboardSnapshot,
  CommentSnapshot,
  ContainerChangeSnapshot,
  ElementSnapshot,
  GraphLinkSnapshot,
  GraphNodeSnapshot,
  MoveSnapshot,
  PropertyChangeSnapshot,
  ReverseLinkSnapshot,
  SmapStyleSnapshot,
  SmapTextChangeSnapshot,
  StyleChangeSnapshot,
  TextChangeSnapshot,
  UndoableCommand
} from './interfaces/command.interface';

import {DeleteCommand} from './commands/delete.command';
import {ClipboardCommand} from './commands/clipboard.command';
import {PasteCommand} from './commands/paste.command';
import {CreateNodeCommand} from './commands/create-node.command';
import {CreateLinkCommand} from './commands/create-link.command';
import {MoveCommand} from './commands/move.command';
import {ChangeContainerCommand} from './commands/change-container.command';
import {UpdateStyleCommand} from './commands/update-style.command';
import {TextChangeCommand} from './commands/text-change.command';
import {ReverseLinkCommand} from './commands/reverse-link.command';
import {PropertyCommand} from './commands/property.command';
import {CompositeCommand} from './commands/composite.command';
import {SmapStyleCommand} from './commands/smap-style.command';
import {SmapTextChangeCommand} from './commands/smap-text-change.command';
import {CommentCommand} from './commands/comment.command';

/**
 * CommandFactoryService provides methods for capturing state and creating commands.
 */
@Injectable({
  providedIn: 'root'
})
export class CommandFactoryService {
  constructor(
    private cyService: CytoscapeService,
    private nodeService: NodeService,
    private mapService: MapService,
    private mapStateService: MapStateService,
    private commentService: CommentService,
    private commentsStateManager: CommentsStateManager
  ) {
  }

  // ============================================
  // State Capture Methods
  // ============================================

  /**
   * Capture the current state of elements by their IDs.
   */
  captureElementSnapshot(elementIds: string[]): ElementSnapshot {
    const cy = this.cyService.cy;
    if (!cy) {
      return {nodes: [], links: []};
    }

    const nodes: GraphNodeSnapshot[] = [];
    const links: GraphLinkSnapshot[] = [];

    elementIds.forEach(id => {
      const element = cy.$(`#${id}`) as any;
      if (element.empty()) return;

      if (element.isNode()) {
        const parent = element.parent();
        nodes.push({
          id,
          data: {...element.data(), id},
          position: {...element.position()},
          parentId: parent.nonempty() ? parent.first().id() : null,
          classes: element.classes()
        });
      } else if (element.isEdge()) {
        const edge = element.first();
        const sourceId = edge.source().id();
        const targetId = edge.target().id();
        links.push({
          id,
          data: {...element.data(), id, source: sourceId, target: targetId},
          sourceId,
          targetId
        });
      }
    });

    return {nodes, links};
  }

  /**
   * Capture elements including all descendants of selected nodes.
   */
  captureElementSnapshotWithDescendants(elementIds: string[]): ElementSnapshot {
    const cy = this.cyService.cy;
    if (!cy) {
      return {nodes: [], links: []};
    }

    const allIds = new Set<string>(elementIds);
    const processedIds = new Set<string>();

    // Expand to include descendants
    elementIds.forEach(id => {
      const element = cy.$(`#${id}`);
      if (element.isNode()) {
        const descendants = element.descendants();
        descendants.forEach((desc: any) => {
          allIds.add(desc.id());
        });
      }
    });

    // Also include connected edges
    allIds.forEach(id => {
      const element = cy.$(`#${id}`);
      if (element.isNode()) {
        const edges = element.connectedEdges();
        edges.forEach((edge: any) => {
          allIds.add(edge.id());
        });
      }
    });

    return this.captureElementSnapshot(Array.from(allIds));
  }

  /**
   * Capture current clipboard state.
   */
  captureClipboardSnapshot(): ClipboardSnapshot {
    return {
      content: localStorage.getItem('clipboard'),
      timestamp: Date.now()
    };
  }

  /**
   * Capture position of nodes.
   */
  capturePositions(nodeIds: string[]): Map<string, { x: number; y: number }> {
    const cy = this.cyService.cy;
    const positions = new Map<string, { x: number; y: number }>();

    if (!cy) return positions;

    nodeIds.forEach(id => {
      const node = cy.$(`#${id}`);
      if (node.length > 0 && node.isNode()) {
        positions.set(id, {...node.position()});
      }
    });

    return positions;
  }

  // ============================================
  // Command Creation Methods
  // ============================================

  /**
   * Create a delete command for the given elements.
   */
  createDeleteCommand(snapshot: ElementSnapshot): DeleteCommand {
    return new DeleteCommand(this.cyService, this.nodeService, snapshot);
  }

  /**
   * Create a copy command.
   */
  createCopyCommand(
    beforeClipboard: ClipboardSnapshot,
    afterClipboard: ClipboardSnapshot,
    elementCount: number,
    nodeCount: number = 0,
    linkCount: number = 0
  ): ClipboardCommand {
    return new ClipboardCommand('copy', beforeClipboard, afterClipboard, elementCount, nodeCount, linkCount);
  }

  /**
   * Create a cut command (composite of copy + delete).
   */
  createCutCommand(
    beforeClipboard: ClipboardSnapshot,
    afterClipboard: ClipboardSnapshot,
    elementsToDelete: ElementSnapshot,
    elementCount: number,
    nodeCount: number = 0,
    linkCount: number = 0
  ): CompositeCommand {
    const copyCommand = new ClipboardCommand('cut', beforeClipboard, afterClipboard, elementCount, nodeCount, linkCount);
    const deleteCommand = new DeleteCommand(this.cyService, this.nodeService, elementsToDelete);

    // Build description based on element types
    let description: string;
    if (nodeCount === 1 && linkCount === 0) {
      description = 'Node Cut';
    } else if (nodeCount === 0 && linkCount === 1) {
      description = 'Link Cut';
    } else {
      description = `${elementCount}-Element Selection Cut`;
    }

    return new CompositeCommand('cut', description, [copyCommand, deleteCommand]);
  }

  /**
   * Create a paste command.
   */
  createPasteCommand(pastedElements: ElementSnapshot): PasteCommand {
    return new PasteCommand(this.cyService, this.nodeService, pastedElements);
  }

  /**
   * Create a create node command.
   */
  createCreateNodeCommand(nodeSnapshot: GraphNodeSnapshot): CreateNodeCommand {
    return new CreateNodeCommand(this.cyService, this.nodeService, nodeSnapshot);
  }

  /**
   * Create a create link command.
   */
  createCreateLinkCommand(linkSnapshot: GraphLinkSnapshot): CreateLinkCommand {
    return new CreateLinkCommand(this.cyService, this.nodeService, linkSnapshot);
  }

  /**
   * Create a move command from before/after positions.
   */
  createMoveCommand(moves: MoveSnapshot[]): MoveCommand {
    return new MoveCommand(this.cyService, this.nodeService, moves);
  }

  /**
   * Create a move command from position maps.
   */
  createMoveCommandFromMaps(
    beforePositions: Map<string, { x: number; y: number }>,
    afterPositions: Map<string, { x: number; y: number }>
  ): MoveCommand | null {
    const moves: MoveSnapshot[] = [];

    beforePositions.forEach((before, nodeId) => {
      const after = afterPositions.get(nodeId);
      if (after && (before.x !== after.x || before.y !== after.y)) {
        moves.push({nodeId, before, after});
      }
    });

    if (moves.length === 0) return null;

    return new MoveCommand(this.cyService, this.nodeService, moves);
  }

  /**
   * Create a change container command.
   */
  createChangeContainerCommand(
    type: 'change-container' | 'extract-from-container' | 'extract-to-canvas',
    changes: ContainerChangeSnapshot[]
  ): ChangeContainerCommand {
    return new ChangeContainerCommand(
      this.cyService,
      this.nodeService,
      this.mapService,
      type,
      changes
    );
  }

  /**
   * Create an update style command.
   */
  createUpdateStyleCommand(
    changes: StyleChangeSnapshot[],
    description?: string
  ): UpdateStyleCommand {
    return new UpdateStyleCommand(this.cyService, this.nodeService, changes, description, this.mapStateService);
  }

  /**
   * Create a text change command.
   */
  createTextChangeCommand(
    entityId: string,
    entityType: 'node' | 'link',
    field: string,
    before: string,
    after: string,
    focusSessionId: string
  ): TextChangeCommand {
    const snapshot: TextChangeSnapshot = {
      entityId,
      entityType,
      field,
      before,
      after
    };

    return new TextChangeCommand(this.cyService, this.nodeService, snapshot, focusSessionId);
  }

  /**
   * Create a reverse link command.
   */
  createReverseLinkCommand(
    linkId: string,
    currentSourceId: string,
    currentTargetId: string
  ): ReverseLinkCommand {
    // Capture full edge data before reverse for proper undo
    const cy = this.cyService.cy;
    let beforeEdgeData: Record<string, unknown> | undefined;
    if (cy) {
      const edge = cy.$(`#${linkId}`);
      if (edge.length > 0) {
        beforeEdgeData = {...edge.data()};
      }
    }

    const snapshot: ReverseLinkSnapshot = {
      linkId,
      beforeSourceId: currentSourceId,
      beforeTargetId: currentTargetId,
      afterSourceId: currentTargetId,  // Swapped
      afterTargetId: currentSourceId,  // Swapped
      beforeEdgeData
    };

    return new ReverseLinkCommand(this.cyService, this.nodeService, snapshot);
  }

  /**
   * Create a property (metadata) command.
   */
  createPropertyCommand(
    entityId: string,
    entityType: 'node' | 'link',
    action: 'add' | 'remove' | 'update',
    beforeMetadata: unknown[],
    afterMetadata: unknown[]
  ): PropertyCommand {
    const snapshot: PropertyChangeSnapshot = {
      entityId,
      entityType,
      action,
      beforeMetadata,
      afterMetadata
    };

    return new PropertyCommand(this.cyService, this.nodeService, snapshot);
  }

  /**
   * Create a composite command from multiple commands.
   */
  createCompositeCommand(
    type: 'composite',
    description: string,
    commands: UndoableCommand[]
  ): CompositeCommand {
    return new CompositeCommand(type, description, commands);
  }

  // ============================================
  // Smap (Map-Level) Commands
  // ============================================

  /**
   * Create a smap style change command.
   */
  createSmapStyleCommand(
    mapId: string,
    beforeStyles: Record<string, unknown>,
    afterStyles: Record<string, unknown>
  ): SmapStyleCommand {
    const snapshot: SmapStyleSnapshot = {
      mapId,
      beforeStyles,
      afterStyles
    };

    return new SmapStyleCommand(this.mapService, snapshot);
  }

  /**
   * Create a smap text change command.
   */
  createSmapTextChangeCommand(
    mapId: string,
    field: 'name' | 'description',
    before: string,
    after: string,
    focusSessionId: string
  ): SmapTextChangeCommand {
    const snapshot: SmapTextChangeSnapshot = {
      mapId,
      field,
      before,
      after
    };

    return new SmapTextChangeCommand(this.mapService, snapshot, focusSessionId);
  }

  // ============================================
  // Comment Commands
  // ============================================

  /**
   * Create a comment add command.
   */
  createCommentAddCommand(
    entityId: string,
    entityType: 'map' | 'node' | 'link',
    content: string,
    parentId?: string
  ): CommentCommand {
    const snapshot: CommentSnapshot = {
      entityId,
      entityType,
      content,
      parentId
    };

    return new CommentCommand(this.commentService, this.commentsStateManager, 'add', snapshot);
  }

  /**
   * Create a comment edit command.
   */
  createCommentEditCommand(
    commentId: string,
    entityId: string,
    entityType: 'map' | 'node' | 'link',
    previousContent: string,
    newContent: string
  ): CommentCommand {
    const snapshot: CommentSnapshot = {
      commentId,
      entityId,
      entityType,
      content: newContent,
      previousContent
    };

    return new CommentCommand(this.commentService, this.commentsStateManager, 'edit', snapshot);
  }

  /**
   * Create a comment remove command.
   */
  createCommentRemoveCommand(
    commentId: string,
    entityId: string,
    entityType: 'map' | 'node' | 'link',
    content: string,
    parentId?: string
  ): CommentCommand {
    const snapshot: CommentSnapshot = {
      commentId,
      entityId,
      entityType,
      content,
      parentId
    };

    return new CommentCommand(this.commentService, this.commentsStateManager, 'remove', snapshot);
  }
}
